import { useCallback, useRef, useEffect } from 'react'
import {
  isRealtimeSuspended,
  onRealtimeVisibilityChange,
} from '../../../lib/utils/realtime-visibility'

export function useEventSource() {
  const sse = useRef<EventSource | null>(null)
  const isConnecting = useRef(false)
  const currentUrl = useRef<string | null>(null)
  const callbackRef = useRef<((data: any) => void) | null>(null)

  const closeConnection = useCallback(() => {
    sse.current?.close()
    sse.current = null
    isConnecting.current = false
  }, [])

  const connect = useCallback(() => {
    const url = currentUrl.current
    if (!url || isRealtimeSuspended()) {
      return
    }

    if (isConnecting.current || (sse.current && sse.current.readyState !== EventSource.CLOSED)) {
      return
    }

    try {
      isConnecting.current = true
      currentUrl.current = url

      sse.current = new EventSource(url, { withCredentials: true })

      sse.current.onopen = function () {
        isConnecting.current = false
      }

      sse.current.onmessage = function (event) {
        try {
          const data = JSON.parse(event?.data)
          if (data) {
            callbackRef.current?.(data)
          }
        } catch (parseErr) {
          console.error('[SSE] Parse error:', parseErr)
        }
      }

      sse.current.onerror = function () {
        isConnecting.current = false
        
        if (sse.current?.readyState === EventSource.CLOSED) {
          closeConnection()
        }
      }
    } catch (err) {
      console.error('[SSE] Subscribe error:', err)
      isConnecting.current = false
      closeConnection()
    }
  }, [closeConnection])

  const subscribe = useCallback(
    (url: string, callback: (data: any) => void) => {
      callbackRef.current = callback

      if (currentUrl.current !== url) {
        closeConnection()
        currentUrl.current = url
      }

      connect()
    },
    [closeConnection, connect],
  )

  useEffect(() => {
    const removeVisibilityListener = onRealtimeVisibilityChange(() => {
      if (isRealtimeSuspended()) {
        closeConnection()
      } else {
        connect()
      }
    })

    return () => {
      removeVisibilityListener()
      closeConnection()
      currentUrl.current = null
      callbackRef.current = null
    }
  }, [closeConnection, connect])

  return { sse, subscribe }
}
